import is from '@sindresorhus/is';
import type { FunctionComponent, MouseEvent } from 'react';
import { Fragment, useState } from 'react';
import { useMediaQuery } from 'usehooks-ts';

import { useTranslate } from '~/shared/context/use-translate';
import type { JobLevel } from '~/shared/data-contract/job-level';
import type { JobRole } from '~/shared/data-contract/job-role';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { Badge } from '~/ui/core/badge/Badge';
import { ConfirmationDialog } from '~/ui/core/confirmation-dialog/ConfirmationDialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconArchive, IconDelete, IconThreeDots } from '~/ui/core/icons';
import type { TableColumnWidth } from '~/ui/core/table/Table';
import { TableActionButton, TableCell, TableRow } from '~/ui/core/table/Table';
import { LARGE_SCREEN_BREAKPOINT } from '~/ui/domain/tiling-engine/constants';

import { useSnackbars } from '../../app/error-handling/SnackBars';
import { useDeleteJobLevels } from '../api/use-delete-job-levels';
import { useDeleteJobRoles } from '../api/use-delete-job-roles';
import { useInvalidateArchitecture } from '../api/use-invalidate-architecture';
import { useUnarchiveJobLevels } from '../api/use-unarchive-job-levels';
import { useUnarchiveJobRoles } from '../api/use-unarchive-job-roles';
import { useJobArchitecturePermissions } from '../context/JobArchitecturePermissionsContext';
import { useJobArchitectureUrlTileActions } from '../url-tiles/use-job-architecture-url-tile-actions';

type ArchivedRoleItemRowProps = {
    readonly item: JobRole;
    readonly jobArchitectureId: string;
    readonly onRestoreSuccess?: () => void;
    readonly type: 'role';
};

type ArchivedLevelItemRowProps = {
    readonly item: JobLevel;
    readonly jobArchitectureId: string;
    readonly onRestoreSuccess?: () => void;
    readonly type: 'level';
};

type ArchivedItemRowProps = ArchivedLevelItemRowProps | ArchivedRoleItemRowProps;

export const ARCHIVED_ITEMS_COLUMN_WIDTHS: ReadonlyArray<TableColumnWidth> = [
    'minmax(12.5rem, 1fr)',
    '6.25rem',
    '6.25rem',
    '7.5rem',
    '7.5rem',
    '3rem',
];

export const ARCHIVED_ITEMS_COMPACT_COLUMN_WIDTHS: ReadonlyArray<TableColumnWidth> = [
    'minmax(12.5rem, 1fr)',
    '6.25rem',
    '6.25rem',
    '3rem',
];

export const ARCHIVED_OPTIONAL_COLUMN_CLASS = 'hidden min-[1920px]:flex';

export const useArchivedItemsColumnWidths = (): ReadonlyArray<TableColumnWidth> => {
    const isLargeScreen = useMediaQuery(LARGE_SCREEN_BREAKPOINT);

    return isLargeScreen ? ARCHIVED_ITEMS_COLUMN_WIDTHS : ARCHIVED_ITEMS_COMPACT_COLUMN_WIDTHS;
};

const ItemTitle = ({ title }: { readonly title: string }) => (
    <span className="truncate" title={title}>
        {title}
    </span>
);

type StatusVariant = 'gray' | 'success' | 'warning';

type StatusKey = 'draft' | 'published' | 'reviewed';

type StatusConfig = {
    readonly statusKey: StatusKey;
    readonly variant: StatusVariant;
};

const getStatusConfig = (item: JobLevel | JobRole): StatusConfig => {
    const isPublished = item.versionInfo?.isPublished ?? false;
    const isReviewed = item.versionInfo?.isReviewed ?? false;

    if (isPublished) {
        return { statusKey: 'published', variant: 'success' };
    }
    if (isReviewed) {
        return { statusKey: 'reviewed', variant: 'warning' };
    }

    return { statusKey: 'draft', variant: 'gray' };
};

const statusTranslationKeys: Record<StatusKey, TranslationKey> = {
    draft: 'pages.job-architecture-v2.job-profile-status-draft',
    published: 'pages.job-architecture-v2.job-profile-status-published',
    reviewed: 'pages.job-architecture-v2.job-profile-status-reviewed',
};

const getReviewerName = (item: JobLevel | JobRole): string => {
    if (is.nullOrUndefined(item.reviewer)) {
        return '-';
    }

    return `${item.reviewer.firstName} ${item.reviewer.lastName}`;
};

const getPublishedByName = (item: JobLevel | JobRole): string => {
    const publishedBy = item.versionInfo?.publishedBy;

    if (is.nullOrUndefined(publishedBy)) {
        return '-';
    }

    return `${publishedBy.firstName} ${publishedBy.lastName}`;
};

export const ArchivedItemRow: FunctionComponent<ArchivedItemRowProps> = ({
    item,
    jobArchitectureId,
    onRestoreSuccess,
    type,
}) => {
    const translate = useTranslate();
    const { canModify } = useJobArchitecturePermissions();
    const { showErrorSnackbar } = useSnackbars();
    const invalidateArchitecture = useInvalidateArchitecture();
    const handleMutationError = (error: Error): void => {
        showErrorSnackbar(error);
        invalidateArchitecture(jobArchitectureId);
    };
    const columnWidths = useArchivedItemsColumnWidths();
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const { openJobProfileTile } = useJobArchitectureUrlTileActions();

    const unarchiveRolesMutation = useUnarchiveJobRoles();
    const unarchiveLevelsMutation = useUnarchiveJobLevels();
    const deleteRolesMutation = useDeleteJobRoles();
    const deleteLevelsMutation = useDeleteJobLevels();

    const isRestoring = type === 'role' ? unarchiveRolesMutation.isPending : unarchiveLevelsMutation.isPending;
    const isDeleting = type === 'role' ? deleteRolesMutation.isPending : deleteLevelsMutation.isPending;

    const handleRestore = (): void => {
        if (type === 'role') {
            unarchiveRolesMutation.mutate(
                {
                    jobArchitectureId,
                    jobRoleIds: [item.id],
                },
                {
                    onSuccess: () => {
                        onRestoreSuccess?.();
                    },
                    onError: handleMutationError,
                },
            );
        } else {
            unarchiveLevelsMutation.mutate(
                {
                    jobArchitectureId,
                    jobLevelIds: [item.id],
                },
                {
                    onSuccess: () => {
                        onRestoreSuccess?.();
                    },
                    onError: handleMutationError,
                },
            );
        }
    };

    const handleDelete = (): void => {
        if (type === 'role') {
            deleteRolesMutation.mutate(
                {
                    jobRoles: [item],
                },
                {
                    onSuccess: () => {
                        setIsDeleteDialogOpen(false);
                    },
                    onError: handleMutationError,
                },
            );
        } else {
            deleteLevelsMutation.mutate(
                {
                    jobLevels: [item],
                },
                {
                    onSuccess: () => {
                        setIsDeleteDialogOpen(false);
                    },
                    onError: handleMutationError,
                },
            );
        }
    };

    const typeLabel =
        type === 'role'
            ? translate('pages.job-architecture-v2.job-role')
            : translate('pages.job-architecture-v2.job-level');

    const statusConfig = getStatusConfig(item);
    const reviewerName = getReviewerName(item);
    const publishedByName = getPublishedByName(item);

    const handleClick = (): void => {
        openJobProfileTile({
            isArchived: true,
            mode: 'view',
            profileId: item.id,
            type: type === 'role' ? 'job-role' : 'job-level',
            version: item.versionInfo?.version,
        });
    };

    return (
        <Fragment>
            <TableRow $columnWidths={columnWidths} className="cursor-pointer hover:bg-gray-100" onClick={handleClick}>
                <TableCell>
                    <ItemTitle title={item.title} />
                </TableCell>
                <TableCell>{typeLabel}</TableCell>
                <TableCell>
                    <Badge variant={statusConfig.variant}>
                        {translate(statusTranslationKeys[statusConfig.statusKey])}
                    </Badge>
                </TableCell>
                <TableCell className={ARCHIVED_OPTIONAL_COLUMN_CLASS}>{reviewerName}</TableCell>
                <TableCell className={ARCHIVED_OPTIONAL_COLUMN_CLASS}>{publishedByName}</TableCell>
                <TableCell
                    className="justify-end"
                    onClick={(event: MouseEvent<HTMLDivElement>) => event.stopPropagation()}
                >
                    {canModify && (
                        <DropdownMenu>
                            <DropdownMenuTrigger>
                                <TableActionButton
                                    data-testid="archived-item-menu"
                                    onClick={(event: MouseEvent<HTMLButtonElement>) => event.stopPropagation()}
                                >
                                    <IconThreeDots />
                                </TableActionButton>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                onClick={(event: MouseEvent<HTMLDivElement>) => event.stopPropagation()}
                            >
                                <DropdownMenuItem
                                    disabled={isRestoring || isDeleting}
                                    onClick={(event: MouseEvent<HTMLDivElement>) => {
                                        event.stopPropagation();
                                        handleRestore();
                                    }}
                                >
                                    <IconArchive size={16} />
                                    {translate('pages.job-architecture-v2.restore')}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    disabled={isRestoring || isDeleting}
                                    onClick={(event: MouseEvent<HTMLDivElement>) => {
                                        event.stopPropagation();
                                        setIsDeleteDialogOpen(true);
                                    }}
                                >
                                    <IconDelete size={16} />
                                    {translate('common.delete')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </TableCell>
            </TableRow>
            <ConfirmationDialog
                cancelButtonLabel={translate('common.cancel')}
                confirmButtonLabel={translate('common.delete')}
                confirmButtonVariant="destructive"
                description={translate('pages.job-architecture-v2.delete-permanently-description')}
                open={isDeleteDialogOpen}
                title={translate('pages.job-architecture-v2.delete-permanently-title')}
                onCancel={() => setIsDeleteDialogOpen(false)}
                onConfirm={handleDelete}
            />
        </Fragment>
    );
};
